import React, { useState, useEffect } from "react";
import { Tooltip, OverlayTrigger, Modal, Button, Form } from "react-bootstrap";
import { FaFileExcel, FaPhone, FaEnvelope } from "react-icons/fa";
import "../Curso.css";

function CursoInfoForm({ curso, onCursoUpdated }) {
  const [showTooltip, setShowTooltip] = useState(null);
  const [cursoLocal, setCursoLocal] = useState(curso);
  const [showAprobacionesModal, setShowAprobacionesModal] = useState(false);
  const [approvedRutsDraft, setApprovedRutsDraft] = useState(new Set());
  const [savingAprobaciones, setSavingAprobaciones] = useState(false);
  const [aprobacionesError, setAprobacionesError] = useState(null);

  // Function to fetch CSRF token from the backend
  const fetchCSRFToken = async () => {
    const apiUrlcsrf = `${process.env.REACT_APP_BASE_API}api/csrf_token/`;
    const response = await fetch(apiUrlcsrf, { credentials: "include" });
    const data = await response.json();
    return data.csrfToken;
  };

  // Keep local curso synced if parent prop changes
  useEffect(() => {
    setCursoLocal(curso);
  }, [curso]);

  if (!curso) {
    return <div>Cargando...</div>;
  }

  const alumnas = Array.isArray(cursoLocal?.elegidas) ? cursoLocal.elegidas : [];
  const totalAlumnas = alumnas.length;
  const totalAprobadas = alumnas.filter((a) => a?.aprobada === "green").length;
  const totalRechazadas = alumnas.filter((a) => a?.aprobada === "red").length;

  const handleMouseEnter = (index) => {
    setShowTooltip(index);
  };

  const handleMouseLeave = () => {
    setShowTooltip(null);
  };

    const handleExportExcel = () => {
    if (cursoLocal?.id) {
      const exportUrl = `${process.env.REACT_APP_BASE_API}api/perfiles/cursos/${cursoLocal.id}/excel/`;
      window.open(exportUrl, "_blank");
    }
  };

    // 3) Open modal and preload current approved set from backend ("green" means approved)
  const openAprobacionesModal = () => {
    const alumnas = Array.isArray(cursoLocal?.elegidas) ? cursoLocal.elegidas : [];
    const initialApproved = new Set(
      alumnas
        .filter((a) => a?.aprobada === "green")
        .map((a) => a.rut)
        .filter(Boolean)
    );

    setApprovedRutsDraft(initialApproved);
    setAprobacionesError(null);
    setShowAprobacionesModal(true);
  };

  const closeAprobacionesModal = () => {
    if (savingAprobaciones) return;
    setShowAprobacionesModal(false);
  };

  const toggleApprovedRut = (rut) => {
    setApprovedRutsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(rut)) next.delete(rut);
      else next.add(rut);
      return next;
    });
  };

  // 4) Save to backend
  const saveAprobaciones = async () => {
    if (!cursoLocal?.id) return;

    setSavingAprobaciones(true);
    setAprobacionesError(null);

    try {
      const csrfToken = await fetchCSRFToken();
      const url = `${process.env.REACT_APP_BASE_API}api/perfiles/cursos/${cursoLocal.id}/aprobaciones/`;

      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
      
      credentials: "include",
        body: JSON.stringify({
          approved_ruts: Array.from(approvedRutsDraft),
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        setAprobacionesError(data?.error || "Error saving approvals.");
        return;
      }

      // Backend returns the refreshed Curso serializer payload
      setCursoLocal(data);
      if (typeof onCursoUpdated === "function") {
        onCursoUpdated(data); // you can pass the updated curso payload
      }
      setShowAprobacionesModal(false);
    } catch (err) {
      setAprobacionesError("Network error saving approvals.");
    } finally {
      setSavingAprobaciones(false);
    }
  };

  return (
    <div className="curso-info">
      <div className="curso-header">
        <h5>
          Alumnas
          <FaFileExcel
            title="Exportar a Excel"
            className="excel-icon"
            onClick={handleExportExcel}
            style={{ cursor: "pointer", marginLeft: "10px", color: "green" }}
          />
        </h5>

        <div className="alumnas-summary">
          <span className="summary-item">
            Total: <strong>{totalAlumnas}</strong>
          </span>

          <span className="summary-item green-glow">
            A: <strong>{totalAprobadas}</strong>
          </span>

          <span className="summary-item red-glow">
            R: <strong>{totalRechazadas}</strong>
          </span>
        </div>

        <div className="alumnas-actions">
          <button
            type="button"
            className="alumnas-edit-btn"
            onClick={openAprobacionesModal}
          >
            Editar Aprobaciones
          </button>
        </div>


      </div>
      <div className="curso-alumnas">
        {cursoLocal.elegidas && cursoLocal.elegidas.length > 0 ? (
          <div className="alumnas-container">
            {cursoLocal.elegidas.map((elegida, index) => (
              <div key={index} className="alumna-row">
                {/* Column 1: Image with Tooltip */}
                <OverlayTrigger
                  placement="top"
                  show={showTooltip === index}
                  overlay={
                    <Tooltip
                      id={`tooltip-${index}`}
                      className="custom-tooltip"
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <strong>{elegida.nombre}</strong>
                      <br />
                      <a href={`/todas/${elegida.rut}`}>Ver perfil</a>
                    </Tooltip>
                  }
                >
                  <div
                    className={`alumna-info ${elegida.aprobada ? `${elegida.aprobada}-glow` : ""}`}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <img
                      src={
                        elegida.imagen ||
                        "https://cdn-redmaestra-endpoint.azureedge.net/static/images/default.png"
                      }
                      alt={elegida.nombre}
                      className="alumna-img"
                    />
                  </div>
                </OverlayTrigger>

                {/* Column 2: Phone */}
                <div className="alumna-name">
                  <div>{elegida.nombre}</div>
                  <div>
                    <strong>Comuna: </strong>
                    {elegida.comuna}
                  </div>
                </div>

                {/* Column 3: Email */}
                <div className="alumna-contact">
                  <a href={`tel:${elegida.celular}`} className="contact-link">
                    <FaPhone /> {elegida.celular}
                  </a>
                  ----
                  <a href={`mailto:${elegida.correo}`} className="contact-link">
                    <FaEnvelope /> {elegida.correo}
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>No hay alumnas registradas</div>
        )}
      </div>
                        {/* Aprobaciones Modal */}
      <Modal show={showAprobacionesModal} onHide={closeAprobacionesModal} centered>
        <Modal.Header closeButton={!savingAprobaciones}>
          <Modal.Title>Editar Aprobaciones</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {aprobacionesError && (
            <div className="aprobaciones-error">{aprobacionesError}</div>
          )}

          {alumnas.length === 0 ? (
            <div>No hay alumnas registradas</div>
          ) : (
            <div className="aprobaciones-list">
              {alumnas.map((a) => (
                <div key={a.rut} className="aprobaciones-row">
                  <Form.Check
                    type="checkbox"
                    id={`aprob-${a.rut}`}
                    checked={approvedRutsDraft.has(a.rut)}
                    onChange={() => toggleApprovedRut(a.rut)}
                    disabled={savingAprobaciones}
                    label={
                      <span className="aprobaciones-label">
                        <strong>{a.nombre}</strong>
                        <span className="aprobaciones-sub">
                          
                          {a.rut ? ` · ${a.rut}` : ""}
                        </span>
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeAprobacionesModal} disabled={savingAprobaciones}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveAprobaciones} disabled={savingAprobaciones}>
            {savingAprobaciones ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

export default CursoInfoForm;
